// ./ui/RedemptionWheel.js
import React, { useRef, useState } from "react";
import { motion, useAnimation } from "framer-motion";
import { Howl } from "howler";

import spinSoundFile from "../assets/sounds/spin.mp3";
import winSoundFile from "../assets/sounds/win.mp3";
import jackpotSoundFile from "../assets/sounds/jackpot.mp3";
import losingSoundFile from "../assets/sounds/losing.mp3";
import { Button } from "./button";

const spinSound = new Howl({ src: [spinSoundFile] });
const winSound = new Howl({ src: [winSoundFile] });
const jackpotSound = new Howl({ src: [jackpotSoundFile] });
const losingSound = new Howl({ src: [losingSoundFile] });

const redemptionSegments = [
  { label: "75k $REGRET", value: 75000 },
  { label: "Free Spin 🎉", value: 0 },
  { label: "Refund 🩞", value: 100000 },
  { label: "50k $REGRET", value: 50000 },
  { label: "Mystery 🪄", value: -1 },
  { label: "Loser ❌", value: 0 },
];

const anglePer = 360 / redemptionSegments.length;

function getCoordinatesForAngle(angle, radius) {
  const rad = (angle - 90) * (Math.PI / 180);
  return {
    x: radius + radius * Math.cos(rad),
    y: radius + radius * Math.sin(rad),
  };
}

export default function RedemptionWheel({ onComplete }) {
  const controls = useAnimation();
  const [hasSpun, setHasSpun] = useState(false);

  const radius = 130;
  const viewBoxSize = radius * 2;

  const spin = () => {
    spinSound.play();

    const index = Math.floor(Math.random() * redemptionSegments.length);
    const pointerAngle = 270;
    const segmentCenterAngle = (index + 0.5) * anglePer;
    const correction = (pointerAngle - segmentCenterAngle + 360 + 90 + 5) % 360;
    const fullSpins = 6;
    const totalRotation = 360 * fullSpins + correction;

    controls.set({ rotate: 0 });

    controls
      .start({
        rotate: totalRotation,
        transition: {
          duration: 3,
          ease: [0.1, 0.8, 0.3, 1],
        },
      })
      .then(() => {
        setHasSpun(true);
        const prize = redemptionSegments[index];

        if (prize.label === "Loser ❌") {
          losingSound.play();
        } else {
          winSound.play();
        }

        onComplete(prize);
      });
  };

  return (
    <div className="relative my-6 flex flex-col items-center">
      {/* Pointer bovenaan */}
      <div className="absolute -top-5 left-1/2 -translate-x-1/2 w-0 h-0 border-l-[10px] border-r-[10px] border-t-[20px] border-l-transparent border-r-transparent border-t-yellow-400 z-10" />

      <motion.svg
        width={viewBoxSize}
        height={viewBoxSize}
        viewBox={`0 0 ${viewBoxSize} ${viewBoxSize}`}
        animate={controls}
        initial={{ rotate: 0 }}
        className="rounded-full shadow-[0_0_30px_#facc15] border-2 border-yellow-500"
      >
        {redemptionSegments.map((segment, i) => {
          const startAngle = i * anglePer;
          const endAngle = startAngle + anglePer;
          const largeArc = anglePer > 180 ? 1 : 0;

          const start = getCoordinatesForAngle(startAngle, radius);
          const end = getCoordinatesForAngle(endAngle, radius);

          const pathData = `
            M ${radius} ${radius}
            L ${start.x} ${start.y}
            A ${radius} ${radius} 0 ${largeArc} 1 ${end.x} ${end.y}
            Z
          `;

          const fill = i % 2 === 0 ? "#fde047" : "#1f2937";
          const textColor = i % 2 === 0 ? "#000000" : "#fde047";

          const midAngle = startAngle + anglePer / 2;
          const textRadius = radius * 0.75;
          const textX = radius + textRadius * Math.cos((midAngle - 90) * (Math.PI / 180));
          const textY = radius + textRadius * Math.sin((midAngle - 90) * (Math.PI / 180));

          return (
            <g key={i}>
              <path d={pathData} fill={fill} />
              <text
                x={textX}
                y={textY}
                fill={textColor}
                fontSize="12"
                fontWeight="bold"
                textAnchor="middle"
                alignmentBaseline="middle"
                transform={`rotate(${midAngle}, ${textX}, ${textY})`}
              >
                {segment.label}
              </text>
            </g>
          );
        })}
      </motion.svg>

      {!hasSpun && (
        <Button
          onClick={spin}
          className="mt-4 bg-yellow-400 text-black font-bold px-4 py-2 rounded shadow-lg"
        >
          Spin Redemption Wheel
        </Button>
      )}
    </div>
  );
}
